class PCPQuickFilters extends HTMLElement {
  constructor() {
    super();
    this.navigationStack = [];
    this.quickFilterParam = 'quick-filters';
    this.levels = ['root', 'second', 'third'];
  }

  connectedCallback() {
    this.rootLevel = this.querySelector('[data-level="root"]');
    this.secondLevel = this.querySelector('[data-level="second"]');
    this.thirdLevel = this.querySelector('[data-level="third"]');
    this.navWrapper = this.querySelector('.pcp-navigation-wrapper');

    this.originalTitleHTML = document.querySelector('h1.collection-hero__title')?.innerHTML || '';

    this.initEventListeners();
    this.restoreStateFromURL();
    this.updateUIFromURL();
  }

  updateTitle() {
    const h1 = document.querySelector('h1.collection-hero__title');
    if (!h1) return;

    if (this.navigationStack.length > 0) {
      const lastFilter = this.navigationStack[this.navigationStack.length - 1];

      const visuallyHiddenSpan = h1.querySelector('.visually-hidden');

      h1.innerHTML = '';
      if (visuallyHiddenSpan) {
        h1.appendChild(visuallyHiddenSpan);
      }

      const textNode = document.createTextNode(lastFilter.title);
      h1.appendChild(textNode);

    } else {
      h1.innerHTML = this.originalTitleHTML;
    }
  }

  restoreStateFromURL() {
    const searchParams = new URLSearchParams(window.location.search);
    this.syncNavigationStackWithURL(searchParams);
    this.syncActiveStatesWithURL(searchParams);
  }

  syncNavigationStackWithURL(searchParams) {
    const quickFilters = searchParams.get(this.quickFilterParam);
    if (!quickFilters) {
      this.navigationStack = [];
      return;
    }

    const currentStackValues = quickFilters.split('+');
    const newStack = [];
    let currentLevel = this.rootLevel;

    for (let filterValue of currentStackValues) {
      if (filterValue === 'all') continue;

      const filterItem = currentLevel.querySelector(`[data-value="${filterValue}"]`);
      if (!filterItem) break;

      const filterName = filterItem.dataset.name;
      const filterLabel = filterItem.dataset.label;
      const nextLevel = this.getNextLevel(currentLevel);

      newStack.push({
        title: filterLabel,
        levelElement: nextLevel,
        filterValue: filterValue,
        filterName: filterName,
        index: newStack.length
      });

      if (nextLevel) {
        currentLevel = nextLevel;
      } else {
        break;
      }
    }

    this.navigationStack = newStack;
  }

  getNextLevel(currentLevel) {
    const currentLevelIndex = this.levels.indexOf(currentLevel.dataset.level);
    if (currentLevelIndex === -1 || currentLevelIndex >= this.levels.length - 1) {
      return null;
    }

    const nextLevelName = this.levels[currentLevelIndex + 1];
    const nextLevel = this.querySelector(`[data-level="${nextLevelName}"]`);

    // Only return next level if it has at least one non-disabled item
    if (nextLevel && !this.levelHasAvailableItems(nextLevel)) {
      return null;
    }

    return nextLevel;
  }

  levelHasAvailableItems(levelElement) {
    if (!levelElement) return false;
    const nonDisabledItems = levelElement.querySelectorAll('.pcp-filter-item:not([disabled])');
    return nonDisabledItems.length > 0;
  }

  syncActiveStatesWithURL(searchParams) {
    this.querySelectorAll('.pcp-filter-item.active').forEach(item => {
      item.classList.remove('active');
    });

    const quickFilters = searchParams.get(this.quickFilterParam);

    if (quickFilters) {
      const filterValues = quickFilters.split('+');
      filterValues.forEach(filterValue => {
        if (filterValue !== 'all') {
          const filterItem = this.querySelector(`[data-value="${filterValue}"]`);
          if (filterItem) {
            filterItem.classList.add('active');
          }
        }
      });
    }

    for (let [key, value] of searchParams.entries()) {
      if (key.startsWith('filter.')) {
        const filterItem = this.querySelector(`[data-value="${value}"]`);
        if (filterItem) {
          filterItem.classList.add('active');
        }
      }
    }
  }

  updateUIFromURL() {
    console.log("Updating UI from URL with navigation stack:", this.navigationStack);
    this.querySelectorAll('.pcp-filters-sublevel').forEach(level => {
      level.classList.remove('active');
      level.classList.add('hidden');
    });

    if (this.navigationStack.length > 0) {
      if (this.navigationStack.length >= 2) {
        this.rootLevel.classList.add('hidden');

        // Use the server-computed data-has-tier3 attribute as the source of truth.
        // This is set by Liquid and reflects whether any tier-3 filter values have
        // count > 0 under the currently active filter combination. This correctly
        // handles collections where a tier-2 value (e.g. Accessories) has no tier-3
        // children, even when other tier-3 options show count > 0 due to unscoped
        // filter counts from the S&D app.
        const hasTier3Attr = this.dataset.hasTier3;
        const thirdLevelHasItems = hasTier3Attr !== undefined
          ? hasTier3Attr === 'true'
          : (this.thirdLevel && this.levelHasAvailableItems(this.thirdLevel));

        if (thirdLevelHasItems) {
          if (this.secondLevel) this.secondLevel.classList.add('hidden');
          this.thirdLevel.classList.remove('hidden');
          this.thirdLevel.classList.add('active');
        } else {
          // Third level has no available items — show second level instead
          if (this.secondLevel) {
            this.secondLevel.classList.remove('hidden');
            this.secondLevel.classList.add('active');
          }
          if (this.thirdLevel) this.thirdLevel.classList.add('hidden');
        }
      }
      else if (this.navigationStack.length === 1) {
        this.rootLevel.classList.add('hidden');
        if (this.secondLevel) {
          this.secondLevel.classList.remove('hidden');
          this.secondLevel.classList.add('active');
        }
      }
    } else {
      this.rootLevel.classList.remove('hidden');
      if (this.secondLevel) this.secondLevel.classList.add('hidden');
      if (this.thirdLevel) this.thirdLevel.classList.add('hidden');
    }

    this.updateTitle();
    this.renderNavigationButtons();
  }

  initEventListeners() {
    this.addEventListener('click', (e) => {
      const filterItem = e.target.closest('.pcp-filter-item');
      const navButton = e.target.closest('.pcp-nav-btn');
      const resetButton = e.target.closest('.pcp-reset');

      if (filterItem && !filterItem.disabled) {
        this.handleFilterClick(filterItem);
      }

      if (navButton) {
        const index = parseInt(navButton.dataset.index, 10);
        this.handleNavigationBack(index);
      }

      if (resetButton) {
        this.resetAllFilters();
      }
    });
  }

  handleFilterClick(filterItem) {
    this.classList.add('loading');
    filterItem.classList.add('loading');
    const currentLevel = filterItem.closest('.pcp-filters-level');
    const nextLevel = this.getNextLevel(currentLevel);

    this.applyFilterWithStack(filterItem, nextLevel);
  }

  applyFilterWithStack(filterItem, nextLevel) {
    const filterValue = filterItem.dataset.value;
    const filterName = filterItem.dataset.name;
    const currentLevel = filterItem.closest('.pcp-filters-level');
    const searchParams = new URLSearchParams(window.location.search);

    const filterValues = searchParams.getAll(filterName);
    const filterIndex = filterValues.indexOf(filterValue);
    const isCurrentlyActive = filterIndex !== -1;

    if (nextLevel) {
      const otherActiveItems = currentLevel.querySelectorAll('.pcp-filter-item.active');
      otherActiveItems.forEach(otherItem => {
        if (otherItem !== filterItem) {
          const otherValue = otherItem.dataset.value;
          const otherName = otherItem.dataset.name;

          searchParams.delete(otherName, otherValue);

          this.removeFromNavigationStack(otherValue);

          otherItem.classList.remove('active');
        }
      });
    }

    if (isCurrentlyActive) {
      if (nextLevel) {
        this.addFilterToStack(filterItem, nextLevel);

        if (this.navigationStack.length > 0) {
          const stackValues = this.navigationStack.map(item => item.filterValue).join('+');
          searchParams.set(this.quickFilterParam, stackValues);
        }
      } else {
        searchParams.delete(filterName, filterValue);
        this.removeFromNavigationStack(filterValue);

        if (this.navigationStack.length > 0) {
          const stackValues = this.navigationStack.map(item => item.filterValue).join('+');
          searchParams.set(this.quickFilterParam, stackValues);
        } else {
          searchParams.delete(this.quickFilterParam);
        }
      }
    } else {
      searchParams.append(filterName, filterValue);

      if (nextLevel) {
        this.addFilterToStack(filterItem, nextLevel);
      }

      if (this.navigationStack.length > 0) {
        const stackValues = this.navigationStack.map(item => item.filterValue).join('+');
        searchParams.set(this.quickFilterParam, stackValues);
      } else {
        searchParams.delete(this.quickFilterParam);
      }
    }

    searchParams.delete('page');
    this.formSubmit(searchParams.toString());
  }

  addFilterToStack(filterItem, nextLevel) {
    const filterValue = filterItem.dataset.value;
    const filterName = filterItem.dataset.name;
    const filterLabel = filterItem.dataset.label;

    this.navigationStack.push({
      title: filterLabel,
      levelElement: nextLevel,
      filterValue: filterValue,
      filterName: filterName,
      index: this.navigationStack.length
    });

    this.updateTitle();
  }

  removeFromNavigationStack(filterValue) {
    console.log("Removing from navigation stack:", filterValue);
    const index = this.navigationStack.findIndex(item => item.filterValue === filterValue);
    if (index !== -1) {
      this.navigationStack = this.navigationStack.slice(0, index);
      this.updateTitle();
    }
  }

  handleNavigationBack(index) {
    this.classList.add('loading');
    const toKeep = this.navigationStack.slice(0, index);
    const toRemove = this.navigationStack.slice(index);

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('page');

    if (toRemove.length > 0) {
      const firstRemovedItem = toRemove[0];
      if (firstRemovedItem.levelElement) {
        const closingLevel = firstRemovedItem.levelElement;
        const filterName = closingLevel.dataset.filterName;

        if (filterName) {
          searchParams.delete(filterName);
        }
      }
    }

    toRemove.forEach(item => {
      if (item.filterName && item.filterValue) {
        searchParams.delete(item.filterName, item.filterValue);
      }
    });

    if (toKeep.length > 0) {
      const stackValues = toKeep.map(item => item.filterValue).join('+');
      searchParams.set(this.quickFilterParam, stackValues);
    } else {
      searchParams.delete(this.quickFilterParam);
    }

    this.navigationStack = toKeep;

    this.updateTitle();

    this.formSubmit(searchParams.toString());
  }

  resetAllFilters() {
    this.classList.add('loading');
    this.navigationStack = [];

    this.updateTitle();

    const searchParams = new URLSearchParams(window.location.search);

    this.levels.forEach(level => {
      const levelElement = this.querySelector(`[data-level="${level}"]`);
      if (levelElement) {
        const filterName = levelElement.dataset.filterName;
        searchParams.delete(filterName);
      }
    });

    searchParams.delete(this.quickFilterParam);
    searchParams.delete('page');
    this.formSubmit(searchParams.toString());
  }

  renderNavigationButtons() {
    this.navWrapper.querySelectorAll('.pcp-nav-btn').forEach((btn) => btn.remove());
    this.navigationStack.forEach((item, index) => {
      const btn = document.createElement('button');
      btn.className = 'pcp-nav-btn quick-filter-reset';
      btn.style.setProperty('--left', `${index + 1}`);
      btn.dataset.index = index;
      btn.textContent = item.title;
      this.navWrapper.appendChild(btn);
    });

    const allButtons = this.navWrapper.querySelectorAll('button');
    const resetButton = this.navWrapper.querySelector('.pcp-reset');

    if (allButtons.length > 1) {
      allButtons.forEach((btn, index) => {
        if (index !== allButtons.length - 1) {
          btn.disabled = true;
        } else {
          btn.disabled = false;
        }
      });
      if (resetButton) {
        resetButton.disabled = true;
      }
    } else {
      if (resetButton) {
        resetButton.disabled = false;
      }
    }
  }

  formSubmit(searchParams) {
    const form = this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
    if (form && typeof form.onSubmitForm === 'function') {
      form.onSubmitForm(searchParams);
    } else {
      window.location.href = window.location.pathname + (searchParams ? `?${searchParams}` : '');
    }
  }
}

customElements.define('pcp-quick-filters', PCPQuickFilters);
